/*
 * useFilteredSchemas Hook
 * 
 * Features:
 * - Filters schemas to show team + all RLP schemas based on user membership and entity type
 * - Applies section-level permission metadata
 * - Handles ordering (team first, RLP second)
 * - Supports both accounts and contacts (with team inheritance)
 * - Uses helper functions for cleaner schema type checking
 * 
 * Implementation:
 * - Returns array of schemas with permission metadata
 * - Team schema visible only to team members
 * - RLP schemas (rlp = 1) visible only for RLP entities (rlpCompanyId not null/0), always read-only
 * - Each schema gets editable flag based on entity and team permissions
 * 
 * Changes:
 * - 2026-01-28: Created for schema visibility feature
 * - 2026-01-30: Added RLP schema filtering based on rlpCompanyId; RLP data is always read-only
 * - 2026-01-30: Refactored to use schema type helper functions for better maintainability
 * - 2026-01-31: Removed base schema concept
 * - 2026-07-20: Retained every accessible RLP schema section on entity details
 * - 2026-07-20: Restored base-before-franchise ordering for RLP detail sections
 */

import { useMemo } from 'react';
import { SchemaWithPermissions } from '../context/SchemaContext';
import { useSchemas } from '../context/SchemaContext';
import { useTeams } from '../context/TeamContext';
import { canEdit } from '../utils/permissions';
import {
  getEntityTeamId,
  isUserTeamMember,
  isTeamSchema,
  isRlpSchema
} from '../utils/schemaUtils';

interface UseFilteredSchemasResult {
  schemas: SchemaWithPermissions[];
  isLoading: boolean;
}

/**
 * Hook to get filtered and permissioned schemas for an entity
 * 
 * @param entityType - 1 for contacts, 2 for accounts
 * @param entity - The account or contact object
 * @returns Filtered schemas with permission metadata
 */
export function useFilteredSchemas(
  entityType: number,
  entity: { teamId?: number | null; rlpCompanyId?: number | null; createdBy?: number | null } | null
): UseFilteredSchemasResult {
  const { schemas: allSchemas, isLoading } = useSchemas(entityType);
  const { teams, currentUserId } = useTeams();

  const filteredSchemas = useMemo(() => {
    if (!entity || !allSchemas.length) {
      return [];
    }

    const result: SchemaWithPermissions[] = [];

    // Get entity's team ID (handles contact inheritance from account)
    const entityTeamId = getEntityTeamId(entity);

    // Check if user can edit this entity at all
    const hasEntityPermission = canEdit(entity, teams, currentUserId);

    // Check if entity is an RLP entity (matches RLP chip display logic: !!rlpCompanyId)
    // Note: Contacts inherit rlpCompanyId from their parent account
    const isRlpEntity = !!entity.rlpCompanyId;

    // 1. Get team schema (if entity has team and user is member)
    if (entityTeamId !== null && entityTeamId !== undefined) {
      const teamSchema = allSchemas.find(s => isTeamSchema(s, entityTeamId));
      const isTeamMember = isUserTeamMember(entityTeamId, teams);

      // Only show team schema if user is a member of that team
      if (teamSchema && isTeamMember) {
        result.push({
          ...teamSchema,
          isEditable: hasEntityPermission && isTeamMember, // Team editable if user can edit entity AND is team member
          displayOrder: 0,
          sectionLabel: teamSchema.name
        });
      }
    }

    // 2. Get all RLP schemas (only for RLP entities, always read-only)
    if (isRlpEntity) {
      // The schema endpoint is alphabetical, which places Franchise before the base RLP
      // section. Reorder detail cards by the established RLP naming convention without
      // changing metadata-driven visibility or list-selector ordering.
      const rlpSchemas = allSchemas
        .filter(isRlpSchema)
        .sort((left, right) => {
          const leftIsBase = /^rlp(?:\s|$)/i.test(left.name.trim());
          const rightIsBase = /^rlp(?:\s|$)/i.test(right.name.trim());

          return Number(rightIsBase) - Number(leftIsBase);
        });

      rlpSchemas.forEach((rlpSchema, index) => {
        result.push({
          ...rlpSchema,
          isEditable: false, // RLP data is read-only (synced from external system)
          displayOrder: index + 1,
          sectionLabel: rlpSchema.name
        });
      });
    }

    return result;
  }, [entity, allSchemas, teams, currentUserId]);

  return {
    schemas: filteredSchemas,
    isLoading
  };
}
